import { Link, useLocation } from "react-router-dom";
import {
  Heart,
  ShoppingBag,
  Store,
  Sparkles,
  Package,
  Plus,
  ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  marketplaceArea,
  marketplaceLinks,
  sellerView,
} from "@/utils/marketplaceNavigation";

import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel } from "@/components/ui/dropdown-menu";

const icons = [ShoppingBag, Store, Sparkles, Package, Plus];

export function MarketplaceNavigation() {
  const { pathname } = useLocation();
  const area = marketplaceArea(pathname);
  return (
    <section
      aria-label="Marketplace workspace"
      className="border-b bg-background"
    >
      <div className="container max-w-7xl py-3">
        <div className="flex items-center justify-between gap-3 mb-2">
          <Link to="/marketplace" className="font-bold text-sm">
            Marketplace
          </Link>
          <Link
            to="/marketplace/for-you?lane=saved"
            className="inline-flex min-h-11 items-center gap-2 text-sm"
          >
            <Heart className="h-4 w-4" />
            Saved listings
          </Link>
          <Link to="/marketplace/offers" className="inline-flex min-h-11 items-center text-sm font-semibold">Offers & Collections</Link>
        </div>
        <nav
          aria-label="Marketplace"
          className="flex gap-1 overflow-x-auto pb-1"
        >
          {marketplaceLinks.map((link) => (
            <Link
              key={link.key}
              to={link.href}
              aria-current={area === link.key ? "page" : undefined}
              className={cn(
                "shrink-0 min-h-11 rounded-lg px-4 py-3 text-sm font-semibold hover:bg-muted",
                area === link.key &&
                  "bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-200",
              )}
            >
              {link.label}
            </Link>
          ))}
        </nav>
      </div>
    </section>
  );
}

export function MarketplaceMobileNavigation() {
  const { pathname } = useLocation();
  const area = marketplaceArea(pathname);
  return (
    <nav
      aria-label="Marketplace mobile"
      className="fixed inset-x-0 bottom-0 z-50 grid grid-cols-5 border-t bg-background px-1 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] lg:hidden"
    >
      {marketplaceLinks.map((link, index) => {
        const Icon = icons[index];
        return (
          <Link
            key={link.key}
            to={link.href}
            aria-current={area === link.key ? "page" : undefined}
            className={cn(
              "flex min-h-12 min-w-0 flex-col items-center justify-center gap-1 rounded-xl text-[11px] font-semibold",
              area === link.key
                ? "bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-200"
                : "text-muted-foreground",
            )}
          >
            <Icon aria-hidden="true" className="h-5 w-5" />
            {index === 1 ? "Shops" : index === 3 ? "Purchases" : link.label}
          </Link>
        );
      })}
    </nav>
  );
}

export function SellerWorkspaceNavigation() {
  const { pathname, search, hash } = useLocation();
  const view = sellerView(search, hash);
  const links = [
    ["Overview", "/seller", pathname === "/seller"],
    [
      "Listings",
      "/seller/listings?view=listings",
      pathname === "/seller/listings" && view === "listings",
    ],
    [
      "Sales",
      "/seller/listings?view=orders",
      pathname === "/seller/listings" && view === "orders",
    ],
    [
      "Payouts",
      "/seller/listings?view=payouts",
      pathname === "/seller/listings" && view === "payouts",
    ],
    ["Seller Pro", "/seller/pro", pathname === "/seller/pro"],
    ["My shop", "/seller/shop", pathname === "/seller/shop"],
    ["Live shows", "/seller/shows", pathname === "/seller/shows"],
    ["Offers & Collections", "/marketplace/offers", pathname === "/marketplace/offers"],
    [
      "Settings",
      "/seller/listings?view=settings",
      pathname === "/seller/listings" && view === "settings",
    ],
  ] as const;
  const primary = links.filter(([label]) => ["Overview", "Listings", "Sales", "My shop", "Live shows"].includes(label));
  const business = links.filter(link => !primary.includes(link));
  const businessActive = business.some(([, , active]) => active);
  return (
    <div className="border-b bg-background">
      <div className="container max-w-7xl py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm font-bold">
            <Link to="/marketplace" className="font-normal text-muted-foreground hover:underline">Marketplace</Link>
            <span aria-hidden="true" className="mx-2 text-muted-foreground">/</span>
            Seller workspace{" "}
            <span className="ml-2 hidden font-normal text-muted-foreground sm:inline">
              Manage your business
            </span>
          </div>
          <Button
            asChild
            className="bg-violet-600 text-white hover:bg-violet-700"
          >
            <Link to="/seller/listings/new">
              <Plus className="mr-2 h-4 w-4" />
              Sell a card
            </Link>
          </Button>
        </div>
        <nav
          aria-label="Seller workspace"
          className="mt-3 flex gap-1 overflow-x-auto pb-1"
        >
          {primary.map(([label, href, active]) => (
            <Link
              key={label}
              to={href}
              aria-current={active ? "page" : undefined}
              className={cn(
                "min-h-11 shrink-0 rounded-lg px-4 py-3 text-sm font-semibold hover:bg-muted",
                active &&
                  "bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-200",
              )}
            >
              {label}
            </Link>
          ))}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" aria-current={businessActive ? "page" : undefined} className={cn("min-h-11 shrink-0 inline-flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold hover:bg-muted", businessActive && "bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-200")}>
                Business <ChevronDown aria-hidden="true" className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60 p-2">
              <DropdownMenuLabel className="text-xs text-muted-foreground">Offers, payouts & account</DropdownMenuLabel>
              {business.map(([label, href, active]) => (
                <DropdownMenuItem key={label} asChild className="min-h-11">
                  <Link to={href} aria-current={active ? "page" : undefined}>{label}</Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>
      </div>
    </div>
  );
}


